import React, { memo, useEffect, useMemo, useState } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useTranslation } from 'react-i18next';
import PCNDocumentationModal from './PCNDocumentationModal';
import { calculateVatAmount } from '../../Utils/GetVatValue';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { PCNCodeTranslationKeys } from '../../Utils/PCN874Constants';
import { computeUniformVatGroupIds, collapseGroupedSameVatDocs } from '../../Utils/pcnGrouping';
import XtramModal, { XtramBadge, XtramButton, XtramSearch } from '../Widgets/XtramModal/XtramModal';
import styles from './PCNXtram.module.scss';

const cx = (...classes) => classes.filter(Boolean).join(' ');

/** Backend display VAT/net (code-1 entries, import R) are absolute; the sign comes from doc.amount. Otherwise computed. */
const getDisplayVatAndNet = (doc) => {
    const rawVat = doc.displayVatAmount ?? doc.DisplayVatAmount;
    const rawNet = doc.displayAmountBeforeVat ?? doc.DisplayAmountBeforeVat;
    if (rawVat != null && rawNet != null) {
        const sign = doc.amount < 0 ? -1 : 1;
        const vatAmount = sign * rawVat;
        const netAmount = sign * rawNet;
        return { vatAmount, netAmount, totalAmount: netAmount + vatAmount };
    }
    const vat = calculateVatAmount(doc.amount, doc.vat, doc.documentDate);
    return { vatAmount: vat, netAmount: doc.amount - vat, totalAmount: doc.amount };
};

/** PCN record type (M, C, R, …): a C+M composite shows as M among incomes and as C among inputs. */
const getDocType = (doc, details, isIncome) => {
    if (doc.pcnCompositeType === 'C+M') return isIncome ? 'M' : 'C';
    const map = isIncome ? details?.incomeType : details?.expenseType;
    return map?.[doc.id] ?? map?.[doc.Id] ?? '-';
};

const formatDate = (value, options) => (value ? new Date(value).toLocaleDateString('he-IL', options) : '-');

const SECTIONS = [
    { key: 'incomes', field: 'incomes', titleKey: 'VATTransactions', tone: styles.toneIncome, isIncome: true },
    { key: 'expenses', field: 'expenses', titleKey: 'VATExpenses', tone: styles.toneExpense, isIncome: false },
    { key: 'equipment', field: 'equipmentTransactions', titleKey: 'EquipmentVAT', tone: styles.toneEquipment, isIncome: false },
];

const COLUMNS = [
    { key: 'entry', labelKey: 'Entry' },
    { key: 'type', labelKey: 'Type' },
    { key: 'reference', labelKey: 'Reference' },
    { key: 'documentDate', labelKey: 'DocumentDate' },
    { key: 'netAmount', labelKey: 'AmountBeforeVAT' },
    { key: 'vatAmount', labelKey: 'VATAmount' },
    { key: 'totalAmount', labelKey: 'TotalAmountIncludingVAT' },
    { key: 'companyId', labelKey: 'CompanyId' },
    { key: 'vatPercentage', labelKey: 'VATPercentage' },
    { key: 'description', labelKey: 'Description' },
];

const DEFAULT_SORT = { orderBy: 'documentDate', order: 'desc' };
const PAGE_SIZES = [25, 50, 100, 250];

/** One table row: the document with its display amounts, labels and lower-cased search text precomputed. */
const toRow = (doc, details, isIncome) => {
    const amounts = getDisplayVatAndNet(doc);
    const time = doc.documentDate ? new Date(doc.documentDate).getTime() : 0;
    const dateLabel = formatDate(doc.documentDate);
    const entryLabel = `(${doc.uploadNumber || 'N/A'}) ${formatDate(doc.documentDate, { month: '2-digit', year: '2-digit' })}`;
    const searchText = [
        doc.reference,
        `${doc.uploadNumber || ''} ${dateLabel}`,
        amounts.netAmount,
        amounts.vatAmount,
        amounts.totalAmount,
        doc.accountCaseId,
        doc.description,
    ].map((part) => String(part ?? '').toLowerCase()).join('\n');
    return {
        doc,
        ...amounts,
        type: getDocType(doc, details, isIncome),
        time: Number.isNaN(time) ? 0 : time,
        dateLabel,
        entryLabel,
        searchText,
    };
};

const SORT_VALUES = {
    entry: (r) => `${r.doc.uploadNumber || ''}_${r.doc.documentDate}`,
    type: (r) => r.type,
    reference: (r) => r.doc.reference || '',
    documentDate: (r) => r.time,
    netAmount: (r) => r.netAmount,
    vatAmount: (r) => r.vatAmount,
    totalAmount: (r) => r.totalAmount,
    companyId: (r) => String(r.doc.accountCaseId || ''),
    vatPercentage: (r) => r.doc.vat || 0,
    description: (r) => r.doc.description || '',
};

const sortRows = (rows, { orderBy, order }) => {
    const value = SORT_VALUES[orderBy];
    if (!value) return rows;
    const dir = order === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
        return cmp * dir;
    });
};

const sumRows = (rows) => rows.reduce(
    (acc, r) => ({ beforeVat: acc.beforeVat + r.netAmount, vat: acc.vat + r.vatAmount, total: acc.total + r.totalAmount }),
    { beforeVat: 0, vat: 0, total: 0 }
);

/** Backend totals are PCN-rounded per document; null when the server did not send them. */
const getBackendTotals = (d) => {
    if (d.incomeVatSum == null && d.expenseVatSum == null && d.equipmentInputVat == null) return null;
    const pair = (vat = 0, sum = 0) => ({ beforeVat: sum - vat, vat, total: sum });
    return {
        incomes: pair(d.incomeVatSum ?? 0, d.incomeSum ?? 0),
        expenses: pair(d.expenseVatSum ?? 0, d.expenseSum ?? 0),
        equipment: pair(d.equipmentInputVat ?? 0, d.equipmentInputSum ?? 0),
    };
};

const Amount = ({ value }) => (
    <span className={cx(styles.num, value < 0 && styles.neg)}>{beautifyNumber(value)}</span>
);

const differs = (a, b) => Math.abs((a ?? 0) - (b ?? 0)) >= 0.01;

/** A total, with the sum calculated from the documents underneath when the backend's rounded figure differs. */
const SummaryValue = ({ value, calculated, t }) => (
    <>
        <Amount value={value} />
        {calculated != null && differs(value, calculated) && (
            <span className={styles.summaryCalc} title={t('PCNCalculatedFromDocuments')}>
                {beautifyNumber(calculated)}
            </span>
        )}
    </>
);

const Pager = ({ t, count, page, size, onPage, onSize }) => {
    if (count <= PAGE_SIZES[0]) return null;
    const from = page * size + 1;
    const to = Math.min(count, (page + 1) * size);
    return (
        <div className={styles.pager}>
            <span>{t('RowsPerPage')}</span>
            <select className={styles.pagerSelect} value={size} onChange={(e) => onSize(Number(e.target.value))}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>{`${from}–${to} ${t('of')} ${count}`}</span>
            <span className={styles.pagerBtns}>
                <XtramButton variant="ghost" small disabled={page === 0} onClick={() => onPage(page - 1)}>
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </XtramButton>
                <XtramButton variant="ghost" small disabled={to >= count} onClick={() => onPage(page + 1)}>
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                </XtramButton>
            </span>
        </div>
    );
};

const DocsTable = ({ t, section, details, sort, onSort, page, size, onPage, onSize, expandedGroups, onToggleGroup, onRowClick }) => {
    const { rows, totals, isIncome } = section;
    const offset = page * size;
    const visible = rows.slice(offset, offset + size);

    const renderCells = (row, isChild) => (
        <>
            <td>{isChild ? `↳ ${row.entryLabel}` : row.entryLabel}
                {!isChild && row.doc._groupedCount > 1 && (() => {
                    const groupKey = row.doc._mergeKey || row.doc._groupId;
                    return (
                        <button
                            type="button"
                            className={styles.groupChip}
                            title={(row.doc._groupedUploads || []).join(', ')}
                            onClick={(e) => { e.stopPropagation(); onToggleGroup(groupKey); }}
                        >
                            {`${t('Grouped')} (${row.doc._groupedCount}) ${expandedGroups.has(groupKey) ? '▲' : '▼'}`}
                        </button>
                    );
                })()}
            </td>
            <td><span className={styles.typePill}>{row.type}</span></td>
            <td>{row.doc.reference || '-'}</td>
            <td>{row.dateLabel}</td>
            <td><Amount value={row.netAmount} /></td>
            <td><Amount value={row.vatAmount} /></td>
            <td><Amount value={row.totalAmount} /></td>
            <td>{row.doc.accountCaseId || '-'}</td>
            <td>{row.doc.vat}%</td>
            <td className={styles.descCell} title={row.doc.description || ''}>{row.doc.description || '-'}</td>
        </>
    );

    return (
        <>
            <div className={styles.tableScroll}>
                <table className={styles.docTable}>
                    <thead>
                        <tr>
                            <th className={styles.rowNum}>#</th>
                            {COLUMNS.map((col) => {
                                const active = sort.orderBy === col.key;
                                const Arrow = active && sort.order === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
                                return (
                                    <th key={col.key}>
                                        <button type="button" className={cx(styles.sortBtn, active && styles.sortActive)} onClick={() => onSort(col.key)}>
                                            {t(col.labelKey)}
                                            {active && <Arrow className={styles.sortIcon} />}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.flatMap((row, i) => {
                            const rowKey = `${section.key}-${row.doc.id ?? row.doc.Id ?? 'x'}-${offset + i}`;
                            const groupKey = row.doc._mergeKey || row.doc._groupId;
                            const children = row.doc._groupedCount > 1 && expandedGroups.has(groupKey)
                                ? (row.doc._children || []).map((child) => toRow(child, details, isIncome))
                                : [];
                            return [
                                <tr key={rowKey} onClick={() => onRowClick(row.doc)}>
                                    <td className={styles.rowNum}>{offset + i + 1}</td>
                                    {renderCells(row, false)}
                                </tr>,
                                ...children.map((child, ci) => (
                                    <tr key={`${rowKey}-child-${child.doc.id ?? child.doc.Id ?? ci}`} className={styles.childRow} onClick={() => onRowClick(child.doc)}>
                                        <td className={styles.rowNum} />
                                        {renderCells(child, true)}
                                    </tr>
                                )),
                            ];
                        })}
                        <tr className={styles.totalRow}>
                            <td colSpan={5}>{t('Total')}</td>
                            <td><Amount value={totals.beforeVat} /></td>
                            <td><Amount value={totals.vat} /></td>
                            <td><Amount value={totals.total} /></td>
                            <td colSpan={3} />
                        </tr>
                    </tbody>
                </table>
            </div>
            <Pager t={t} count={rows.length} page={page} size={size} onPage={onPage} onSize={onSize} />
        </>
    );
};

const PCNReportDetailsModal = ({ open, onClose, pcnReportDetails, onDocumentClick, direction = 'rtl' }) => {
    const { t } = useTranslation();
    const [showDocModal, setShowDocModal] = useState(false);
    const [activeSection, setActiveSection] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [sorts, setSorts] = useState({});
    const [pages, setPages] = useState({});
    const [pageSizes, setPageSizes] = useState({});
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());

    // Groups whose documents share one VAT rate collapse into a single row, as the backend reports them.
    const sections = useMemo(() => {
        if (!pcnReportDetails) return [];
        const uniformVatGroupIds = computeUniformVatGroupIds(pcnReportDetails);
        return SECTIONS.map((def) => {
            const raw = pcnReportDetails[def.field] || [];
            const rows = collapseGroupedSameVatDocs(raw, uniformVatGroupIds).map((doc) => toRow(doc, pcnReportDetails, def.isIncome));
            return { ...def, raw, rows, calculated: sumRows(raw.map(getDisplayVatAndNet)) };
        }).filter((s) => s.raw.length > 0);
    }, [pcnReportDetails]);

    const query = searchQuery.trim().toLowerCase();

    const filteredSections = useMemo(() => sections.map((section) => {
        const rows = section.rows.filter((r) =>
            (!query || r.searchText.includes(query)) && (!selectedTypes.length || selectedTypes.includes(r.type)));
        return { ...section, rows: sortRows(rows, sorts[section.key] || DEFAULT_SORT), totals: sumRows(rows) };
    }), [sections, query, selectedTypes, sorts]);

    const typeStats = useMemo(() => {
        const stats = {};
        sections.forEach((section) => section.rows.forEach((r) => {
            if (!r.type || r.type === '-') return;
            const s = stats[r.type] || (stats[r.type] = { count: 0, beforeVat: 0, vat: 0, total: 0 });
            s.count += 1;
            s.beforeVat += r.netAmount;
            s.vat += r.vatAmount;
            s.total += r.totalAmount;
        }));
        return Object.entries(stats).sort((a, b) => a[0].localeCompare(b[0]));
    }, [sections]);

    useEffect(() => {
        setPages({});
    }, [query, selectedTypes, activeSection]);

    useEffect(() => {
        if (open) return;
        setSearchQuery('');
        setSelectedTypes([]);
        setActiveSection('all');
        setExpandedGroups(new Set());
    }, [open]);

    useEffect(() => {
        setExpandedGroups(new Set());
        setPages({});
    }, [pcnReportDetails]);

    if (!pcnReportDetails) return null;

    const backendTotals = getBackendTotals(pcnReportDetails);
    const totalsOf = (key) => {
        const section = sections.find((s) => s.key === key);
        return backendTotals?.[key] || section?.calculated || { beforeVat: 0, vat: 0, total: 0 };
    };
    const calculatedOf = (key) => (backendTotals ? sections.find((s) => s.key === key)?.calculated ?? null : null);

    const incomesVat = totalsOf('incomes').vat;
    const expensesVat = totalsOf('expenses').vat;
    const equipmentVat = totalsOf('equipment').vat;
    const netVat = incomesVat - (expensesVat + equipmentVat);
    const isRefund = netVat < 0;

    const toggleType = (type) => setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]));
    const clearFilters = () => {
        setSearchQuery('');
        setSelectedTypes([]);
    };
    const hasFilters = !!query || selectedTypes.length > 0;

    const toggleGroup = (key) => setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const handleSort = (sectionKey, field) => setSorts((prev) => {
        const current = prev[sectionKey] || DEFAULT_SORT;
        const order = current.orderBy === field && current.order === 'asc' ? 'desc' : 'asc';
        return { ...prev, [sectionKey]: { orderBy: field, order } };
    });

    const handleRowClick = (doc) => onDocumentClick?.({ id: doc.id, documentType: doc.docType });

    const shownSections = filteredSections.filter((s) => activeSection === 'all' || s.key === activeSection);
    const filteredCount = filteredSections.reduce((n, s) => n + s.rows.length, 0);

    return (
        <>
            <PCNDocumentationModal open={showDocModal} onClose={() => setShowDocModal(false)} />
            <XtramModal
                open={open}
                onClose={onClose}
                title={t('PCNReportDetails', { clientName: pcnReportDetails.clientName || '' })}
                direction={direction}
                expanded
                footer={(
                    <>
                        <div className={styles.footerSpacer} />
                        <XtramButton variant="secondary" onClick={onClose}>{t('Close')}</XtramButton>
                    </>
                )}
            >
                <div className={styles.stack}>
                    <div className={styles.summaryGrid}>
                        {sections.map((section) => {
                            const totals = totalsOf(section.key);
                            const calc = calculatedOf(section.key);
                            return (
                                <div key={section.key} className={cx(styles.summaryCard, section.tone)}>
                                    <div className={styles.summaryTitle}>
                                        <span>{t(section.titleKey)}</span>
                                        <XtramBadge>{section.raw.length}</XtramBadge>
                                    </div>
                                    <table className={styles.summaryRows}>
                                        <tbody>
                                            <tr><th>{t('AmountBeforeVAT')}</th><td><SummaryValue t={t} value={totals.beforeVat} calculated={calc?.beforeVat} /></td></tr>
                                            <tr><th>{t('VATAmount')}</th><td><SummaryValue t={t} value={totals.vat} calculated={calc?.vat} /></td></tr>
                                            <tr><th>{t('TotalAmountIncludingVAT')}</th><td><SummaryValue t={t} value={totals.total} calculated={calc?.total} /></td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                        <div className={cx(styles.summaryCard, isRefund ? styles.toneRefund : styles.tonePay)}>
                            <div className={styles.summaryTitle}>
                                <span>{isRefund ? t('VATToRefund') : t('VATToPay')}</span>
                            </div>
                            <div className={styles.netValue}>{beautifyNumber(Math.abs(netVat))}</div>
                            <table className={styles.summaryRows}>
                                <tbody>
                                    <tr><th>{t('IncomesVAT')}</th><td><Amount value={incomesVat} /></td></tr>
                                    <tr><th>{t('ExpensesVAT')}</th><td><Amount value={expensesVat} /></td></tr>
                                    <tr><th>{t('EquipmentVAT')}</th><td><Amount value={equipmentVat} /></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {typeStats.length > 0 && (
                        <div className={styles.typeStrip} role="group" aria-label={t('PCNByType')}>
                            {typeStats.map(([code, s]) => {
                                const active = selectedTypes.includes(code);
                                return (
                                    <button
                                        key={code}
                                        type="button"
                                        aria-pressed={active}
                                        className={cx(styles.typeCard, active && styles.typeCardActive)}
                                        onClick={() => toggleType(code)}
                                        title={`${t('AmountBeforeVAT')}: ${beautifyNumber(s.beforeVat)} · ${t('VATAmount')}: ${beautifyNumber(s.vat)}`}
                                    >
                                        <span className={styles.typeCardHead}>
                                            <span className={styles.typeCode}>{code}</span>
                                            <span className={styles.muted}>{s.count} {t('items')}</span>
                                        </span>
                                        <span className={styles.typeDesc}>{t(PCNCodeTranslationKeys[code] || code)}</span>
                                        <span className={cx(styles.typeAmount, s.total < 0 && styles.neg)}>{beautifyNumber(s.total)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className={styles.toolbar}>
                        <XtramSearch
                            direction={direction}
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder={t('Search')}
                            className={styles.search}
                        />
                        <div className={styles.segmented}>
                            <button
                                type="button"
                                className={cx(styles.segment, activeSection === 'all' && styles.segmentActive)}
                                onClick={() => setActiveSection('all')}
                            >
                                {t('All')}
                                <span className={styles.segmentCount}>{filteredCount}</span>
                            </button>
                            {filteredSections.map((section) => (
                                <button
                                    key={section.key}
                                    type="button"
                                    className={cx(styles.segment, activeSection === section.key && styles.segmentActive)}
                                    onClick={() => setActiveSection(section.key)}
                                >
                                    {t(section.titleKey)}
                                    <span className={styles.segmentCount}>{section.rows.length}</span>
                                </button>
                            ))}
                        </div>
                        {selectedTypes.map((type) => (
                            <XtramButton key={type} variant="ghost" small active onClick={() => toggleType(type)}>
                                {type}
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </XtramButton>
                        ))}
                        {hasFilters && (
                            <XtramButton variant="ghost" small onClick={clearFilters}>{t('Clear')}</XtramButton>
                        )}
                        <div className={styles.footerSpacer} />
                        <XtramButton variant="ghost" small onClick={() => setShowDocModal(true)}>
                            <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
                            {t('ViewPcnDocumentation')}
                        </XtramButton>
                    </div>

                    {shownSections.map((section) => (
                        <div key={section.key} className={cx(styles.panel, section.tone)}>
                            <div className={styles.panelHeader}>
                                <span className={styles.tableTitle}>{t(section.titleKey)}</span>
                                <XtramBadge>{section.rows.length}</XtramBadge>
                            </div>
                            {section.rows.length ? (
                                <DocsTable
                                    t={t}
                                    section={section}
                                    details={pcnReportDetails}
                                    sort={sorts[section.key] || DEFAULT_SORT}
                                    onSort={(field) => handleSort(section.key, field)}
                                    page={pages[section.key] || 0}
                                    size={pageSizes[section.key] || PAGE_SIZES[0]}
                                    onPage={(p) => setPages((prev) => ({ ...prev, [section.key]: p }))}
                                    onSize={(n) => {
                                        setPageSizes((prev) => ({ ...prev, [section.key]: n }));
                                        setPages((prev) => ({ ...prev, [section.key]: 0 }));
                                    }}
                                    expandedGroups={expandedGroups}
                                    onToggleGroup={toggleGroup}
                                    onRowClick={handleRowClick}
                                />
                            ) : (
                                <div className={styles.empty}>{t('NoData')}</div>
                            )}
                        </div>
                    ))}
                </div>
            </XtramModal>
        </>
    );
};

export default memo(PCNReportDetailsModal);
